<template>
    <article>
        <p>
            Distributed request tracing is a popular method for monitoring distributed systems
            because it allows you to see the specific execution stages of any request, such as calls to other services and databases.
            However, the costs of integrating can be significantly high, since it requires changing the code of every component.
            Additionally, it's practically impossible to achieve 100% coverage, due to the fact that many third-party components
            don't support such instrumentation.
        </p>

        <p>
            To address said disadvantages, we implemented eBPF-based container tracing which is a part
            of our open source Prometheus exporter <a href="https://github.com/coroot/coroot-node-agent" target="_blank" rel="noopener noreferrer">node-agent</a>.

            It passively monitors all TCP connections on a node, associates every connection with the related container, and exports metrics in Prometheus format:
        </p>

        <pre v-hl.sh>
# HELP container_net_tcp_successful_connects_total Total number of successful TCP connects
# TYPE container_net_tcp_successful_connects_total counter
container_net_tcp_successful_connects_total{actual_destination="10.128.0.43:443",container_id="/k8s/default/prometheus-0/prometheus-server",destination="10.52.0.1:443"} 2

# HELP container_net_tcp_active_connections Number of active outbound connections used by the container
# TYPE container_net_tcp_active_connections gauge
container_net_tcp_active_connections{actual_destination="10.128.0.43:443",container_id="/k8s/default/prometheus-0/prometheus-server",destination="10.52.0.1:443"} 1

# HELP container_net_tcp_failed_connects_total Total number of failed TCP connects
# TYPE container_net_tcp_failed_connects_total counter
container_net_tcp_failed_connects_total{container_id="/k8s/kube-system/konnectivity-agent-56cdbd78f-f7r7j/konnectivity-agent",destination="10.48.2.2:10250"} 20

# HELP container_net_tcp_listen_info Listen address of the container
# TYPE container_net_tcp_listen_info gauge
container_net_tcp_listen_info{container_id="/k8s/default/paymentservice-5849646947-b744v/server",listen_addr="10.48.0.4:50051",proxy=""} 1
        </pre>

        <p>
            These metrics are obtained using the <var>sock:inet_sock_set_state</var> kernel tracepoint.
            As the name implies, this tracepoint is called whenever a TCP connection changes its state.
        </p>

        <h2>Connection establishing</h2>

        <p>
            First, let's recall the TCP state transitions while establishing an outbound connection:
        </p>

        <v-img max-width="80%" class="ma-auto" src="/static/img/blog/tcp_states_connect.svg" />

        <p>
            As seen in the diagram, a connection can be considered successfully established if the transition <var>SYN_SENT</var> -&gt; <var>ESTABLISHED</var> has occurred.
            Conversely, <var>SYN_SENT</var> -&gt; <var>CLOSED</var> means a failed connection attempt.
            Now, the tricky thing is that an eBPF program can only get the <var>PID</var> of the connection initiator during the
            <var>CLOSED</var> -&gt; <var>SYN_SENT</var> transition.
            The solution for that is saving the initiator's <var>PID</var> to a kernel space map using the socket's
            pointer as a key, then we can use it on further transitions.
        </p>
        <pre v-hl.c>
// saving the connection initiator's PID
if (args.oldstate == BPF_TCP_CLOSE && args.newstate == BPF_TCP_SYN_SENT) {
    struct sk_info i = {};
    i.pid = bpf_get_current_pid_tgid() >> 32;
    bpf_map_update_elem(&sk_info, &args.skaddr, &i, BPF_ANY);
    return 0;
}

...

//getting the PID
__u32 pid = 0;
struct sk_info *i = bpf_map_lookup_elem(&sk_info, &args.skaddr);
if (!i) {
    return 0;
}
pid = i->pid;
bpf_map_delete_elem(&sk_info, &args.skaddr);
        </pre>

        The full source code of the <var>inet_sock_set_state</var> handler can be found <a href="https://github.com/coroot/coroot-node-agent/blob/92ef04b486daf0703591edd7526b34fdb4d9cf54/ebpftracer/ebpf/tcp_state.c#L55" target="_blank" rel="noopener noreferrer">here</a>.

        <h2>Connection closing</h2>

        <v-img max-width="80%" class="ma-auto mb-4" src="/static/img/blog/tcp_states_close.svg" />

        <p>
            Depending on which peer closes the connection, either an <var>ESTABLISHED</var> -&gt; <var>FIN_WAIT1</var> (active close) or
            an <var>ESTABLISHED</var> -&gt; <var>CLOSE_WAIT</var> (passive close) transition will occur.
            The only thing worth noting here is that we cannot get a <var>PID</var> when it's in a passive close, so we decided to
            resolve this in the userspace code. Since the handler does not need to differentiate between active and passive closes,
            it triggers in both cases.
        </p>

        <pre v-hl.c>
if (args.oldstate == BPF_TCP_ESTABLISHED && (args.newstate == BPF_TCP_FIN_WAIT1 || args.newstate == BPF_TCP_CLOSE_WAIT)) {
    pid = 0;
    type = EVENT_TYPE_CONNECTION_CLOSE;
}
        </pre>

        <h2>TCP LISTEN</h2>

        <p>
            In addition to outgoing connection tracing,
            we need to discover all listening sockets and containers associated with them.
            This can also be done by handling the <var>CLOSED</var> -&gt; <var>LISTEN</var> and
            <var>LISTEN</var> -&gt; <var>CLOSED</var> transitions. The initiator's <var>PID</var> is available in both cases.
        </p>

        <pre v-hl.c>
if (args.oldstate == BPF_TCP_CLOSE && args.newstate == BPF_TCP_LISTEN) {
    type = EVENT_TYPE_LISTEN_OPEN;
}
if (args.oldstate == BPF_TCP_LISTEN && args.newstate == BPF_TCP_CLOSE) {
    type = EVENT_TYPE_LISTEN_CLOSE;
}
        </pre>

        <p>
            If the IP address of a listen socket is unspecified (<var>0.0.0.0</var>) the agent replaces it with the IP addresses
            assigned to the corresponding network namespace.
        </p>

        <h2>Network Address Translation (NAT)</h2>

        <p>
            Each event sent by the eBPF-program to the agent contains the source and destination <var>IP:PORT</var> pairs.
            However, the destination address can be virtual, as in the case of Kubernetes services. In this case,
            <var>ClusterIP</var> of the service will be replaced by the IP of a particular Pod by <var>iptables</var> or <var>IPVS</var>.
        </p>

        <v-img max-width="80%" class="ma-auto mb-4" src="/static/img/blog/k8s_service_nat.svg" />

        <p>
            Since we need to know the services that each container interacted with as well as the specific containers involved in this interaction,
            the agent resolves the actual destination of each connection by querying the <var>conntrack</var> table using the Netlink protocol.
            As a result, each metric contains both <var>destination</var> and <var>actual_destination</var> labels:
        </p>

        <pre v-hl.sh>
{destination="10.96.0.1:80", actual_destination="192.168.1.11:80", container_id="/k8s/default/client-pod/server"}
        </pre>

        <h2>Initialization</h2>

        <p>
            In addition to being notified of every new connection, the agent needs to detect all connections
            that were established before it started.
            To do this, it reads the information about the established connections from <var>/proc/net/tcp</var> and
            <var>/proc/net/tcp6</var> in each network namespace on startup.
            The inode of each connection from these files is mapped with <var>/proc/&lt;pid&gt;/fd</var> to find the corresponding container.
        </p>

        <h2>How we use these metrics</h2>

        <ul>
            <li>
                The <var>container_net_tcp_successful_connects_total</var> and <var>container_net_tcp_active_connections</var>
                metrics show which IP:PORT each container is communicating with.
            </li>
            <li>
                The <var>container_net_tcp_listen_info</var> metric shows on which IP:PORT each container is accepting inbound TCP connections.
            </li>
        </ul>

        <p>
            So, joining these metrics by <var>IP:PORT</var> allows us to build a map of container-to-container communications.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/building-a-service-map-using-ebpf/container-to-container.png" />
        </v-card>

        <p>
            To build a map of service-to-service communications, Coroot aggregates individual containers into
            applications, such as <var>Deployments</var> or <var>StatefulSets</var> by using metrics from the <a href="https://github.com/kubernetes/kube-state-metrics" target="_blank" rel="noopener noreferrer">kube-state-metrics</a> exporter.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/building-a-service-map-using-ebpf/service-to-service.png" />
        </v-card>

        <p>
            A service map like this can give you an overview of the distributed system architecture.
        </p>

        <p>
            Here I've only described a portion of the network metrics needed to build a service map.
            Next time we will talk about metrics, which are extremely useful for troubleshooting network-related issues.
            Stay tuned!
        </p>



        <h2>Useful links</h2>

        <ul>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Get started with Coroot</router-link>
            </li>
            <li>
                <a href="https://github.com/coroot/coroot-node-agent" target="_blank" rel="noopener noreferrer">node-agent <v-icon dense>mdi-github</v-icon></a> (Apache-2.0 License)
            </li>
        </ul>
    </article>
</template>

<script>

export default {
}
</script>

<style scoped>
</style>
